import { useAuth, useClerk, useSignIn } from "@clerk/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AuthScreen } from "@/components/auth/auth-screen";
import { InlineError } from "@/components/auth/inline-error";
import { TextLink } from "@/components/auth/text-link";
import { authPath, redirectAfterAuth } from "@/lib/auth-helpers";
import { finishSignIn } from "@/lib/finish-sign-in";

const SSO_FAILED = "Single sign-on could not be completed.";

const taskPath = (key: string | undefined): string | null => {
  if (key === "choose-organization") {
    return "/session-tasks/choose-organization";
  }
  if (key === "reset-password") {
    return "/session-tasks/reset-password";
  }
  return null;
};

const SsoCallbackPage = () => {
  const navigate = useNavigate();
  const clerk = useClerk();
  const { isLoaded, isSignedIn } = useAuth();
  const { signIn } = useSignIn();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);
  const leaving = useRef(false);

  const leave = useCallback(() => {
    if (leaving.current) {
      return;
    }
    leaving.current = true;
    const task = taskPath(clerk.session?.currentTask?.key);
    if (task) {
      navigate(authPath(task), { replace: true });
      return;
    }
    window.location.replace(redirectAfterAuth());
  }, [clerk.session, navigate]);

  useEffect(() => {
    if (!(isLoaded && isSignedIn)) {
      return;
    }
    leave();
  }, [isLoaded, isSignedIn, leave]);

  useEffect(() => {
    if (!(isLoaded && clerk.loaded) || started.current || isSignedIn) {
      return;
    }
    started.current = true;

    clerk
      .handleRedirectCallback(
        {
          continueSignUpUrl: authPath("/signup"),
          firstFactorUrl: authPath("/login/password"),
          reloadResource: "signIn",
          resetPasswordUrl: authPath("/login/reset/password"),
          secondFactorUrl: authPath("/login"),
          signInForceRedirectUrl: redirectAfterAuth(),
          signInUrl: authPath("/login"),
          signUpForceRedirectUrl: redirectAfterAuth(),
          signUpUrl: authPath("/signup"),
          verifyEmailAddressUrl: authPath("/signup/verify"),
        },
        async (to) => {
          if (
            signIn.status === "needs_client_trust" ||
            signIn.status === "needs_second_factor" ||
            signIn.status === "needs_new_password" ||
            signIn.status === "needs_protect_check"
          ) {
            const message = await finishSignIn(signIn, navigate);
            if (message) {
              setError(message);
            }
            return;
          }
          if (clerk.session) {
            leave();
            return;
          }
          if (to.startsWith("http")) {
            window.location.assign(to);
            return;
          }
          navigate(to, { replace: true });
        }
      )
      .then(() => {
        if (clerk.session) {
          leave();
        }
      })
      .catch(() => {
        setError(SSO_FAILED);
      });
  }, [clerk, isLoaded, isSignedIn, leave, navigate, signIn]);

  return (
    <AuthScreen subtitle="Completing single sign-on…" title="Signing you in">
      {error ? (
        <div className="flex flex-col gap-4">
          <InlineError message={error} />
          <p className="m-0 text-[15px] text-n-600">
            <TextLink to={authPath("/login")}>Back to sign in</TextLink>
          </p>
        </div>
      ) : null}
    </AuthScreen>
  );
};

export default SsoCallbackPage;
